/**
 * Budowanie osadzenia dla bloku IFRAME_PARENT (AT-9147).
 *
 * `block.content` to dane najemcy. Wcześniej adres był sklejany do napisu
 * `<iframe src='...'>` i wstawiany przez `innerHTML`, więc apostrof w adresie wychodził
 * z atrybutu (np. `onload=`), a `javascript:` w `src` wykonywał się w originie strony.
 * Teraz adres jest walidowany (tylko `https:`, poprawna nazwa hosta), identyfikatory
 * YouTube są kodowane, a element powstaje przez DOM (`setAttribute`), bez parsowania HTML.
 */

const YOUTUBE_URL_PATTERN = /(https?:\/\/)?(www\.)?(youtube|youtu|youtube-nocookie)\.(com|be)\/.+/;
const IFRAME_SRC_PATTERN = /<iframe.*src=["']([^"']*)["'].*<\/iframe>/;
const HOSTNAME_PATTERN = /^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/i;

/** Zwraca bezpieczny adres `https:` albo null. Adres bez schematu traktujemy jako https. */
export function sanitizeEmbedUrl(raw: string): string | null {
  const trimmed = (raw || '').trim();
  if (trimmed === '') {
    return null;
  }

  let candidate = trimmed;
  if (candidate.startsWith('//')) {
    candidate = `https:${candidate}`;
  } else if (!/^[a-z][a-z0-9+.-]*:/i.test(candidate)) {
    candidate = `https://${candidate}`;
  }

  try {
    const url = new URL(candidate);
    if (url.protocol !== 'https:' || url.username !== '' || url.password !== '' || !HOSTNAME_PATTERN.test(url.hostname)) {
      return null;
    }

    return url.href;
  } catch {
    return null;
  }
}

/** Wyznacza adres osadzenia z treści bloku (kod `<iframe>`, link YouTube albo goły adres). */
export function resolveEmbedUrl(content: string): string | null {
  if (!content) {
    return null;
  }

  const iframeMatch = content.match(IFRAME_SRC_PATTERN);
  if (iframeMatch && iframeMatch[1]) {
    return sanitizeEmbedUrl(iframeMatch[1]);
  }

  if (!YOUTUBE_URL_PATTERN.test(content)) {
    return null;
  }

  const videoId = content.match(/watch\?v=([^&]*)/);
  const listId = content.match(/list=([^&]*)/);

  if (videoId && listId) {
    return sanitizeEmbedUrl(`https://www.youtube.com/embed/${encodeURIComponent(videoId[1])}?list=${encodeURIComponent(listId[1])}`);
  }
  if (videoId) {
    return sanitizeEmbedUrl(`https://www.youtube.com/embed/${encodeURIComponent(videoId[1])}`);
  }

  return sanitizeEmbedUrl(content);
}

/** Tworzy element iframe z już zwalidowanym adresem; żadnego `srcdoc` ani atrybutów zdarzeń. */
export function buildEmbedIframe(doc: Document, url: string): HTMLIFrameElement {
  const iframe = doc.createElement('iframe');
  iframe.setAttribute('src', url);
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  iframe.setAttribute('allowfullscreen', '');
  iframe.style.width = 'inherit';
  iframe.style.height = 'inherit';

  return iframe;
}
